import { Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { ReportSentilinkScores } from 'dashboard/models/report/integration/sentilink-scores';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import Help from 'images/icons/help.svg';

const RiskAssessmentTH = styled(TableTH)`
  width: 320px;
`;

const StyledHelpIcon = styled(Help)`
  width: ${Spacing[4]}px;
  height: ${Spacing[4]}px;
  vertical-align: middle;

  path {
    transition-duration: 150ms;
    stroke: ${({ theme }) => theme.color.text.tertiary};
  }

  :hover {
    path {
      stroke: ${({ theme }) => theme.color.primary.brand};
    }
  }
`;

const redactedSticker = <Sticker>Redacted</Sticker>;

const MEDIUM_RISK_THRESHOLD = 451;
const HIGH_RISK_THRESHOLD = 750;
const MAX_SENTILINK_SCORE = 1000;

const getIcon = (score: number) => {
  if (score < 0) {
    return <></>;
  } else if (score < MEDIUM_RISK_THRESHOLD) {
    return <MatchIconPositive />;
  } else if (score < HIGH_RISK_THRESHOLD) {
    return <MatchIconWarning />;
  } else {
    return <MatchIconAlert />;
  }
};

interface SentilinkScoresProps {
  report: ReportSentilinkScores;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const SentilinkScores: VFC<SentilinkScoresProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: SentilinkScoresProps) => {
  let [
    sentilinkAbuseScore,
    sentilinkIdTheftScore,
    sentilinkFirstPartySyntheticScore,
    sentilinkThirdPartySyntheticScore,
  ] = [-1, -1, -1, -1];

  if (report.attributes.result && report.attributes.result.scores) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    sentilinkAbuseScore = report.attributes.result.scores.find(
      (score) => score.name === 'sentilink_abuse_score',
    ).score;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    sentilinkIdTheftScore = report.attributes.result.scores.find(
      (score) => score.name === 'sentilink_id_theft_score',
    ).score;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    sentilinkFirstPartySyntheticScore = report.attributes.result.scores.find(
      (score) => score.name === 'sentilink_first_party_synthetic_score',
    ).score;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    sentilinkThirdPartySyntheticScore = report.attributes.result.scores.find(
      (score) => score.name === 'sentilink_third_party_synthetic_score',
    ).score;
  }
  const riskAssessmentContents = (
    <>
      <Card.Title>Risk Assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <TableTH data-test='risk-assessment-table__value-header'>Value</TableTH>
          </>
        }
      >
        <TableTR
          data-test='risk-assessment-table__row--abuse-score'
          id='risk-assessment-table__row--abuse-score'
          key='abuse-score'
        >
          <TableIconTD>{getIcon(sentilinkAbuseScore)}</TableIconTD>
          <TableTD>
            <span>Abuse Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              Likelihood that the application identity is an instance of synthetic identity fraud.
            </HelpIcon>
          </TableTD>
          <TableTD>
            {isRedacted ? redactedSticker : `${sentilinkAbuseScore} / ${MAX_SENTILINK_SCORE}`}
          </TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--id-theft-score'
          id='risk-assessment-table__row--id-theft-score'
          key='id-theft-score'
        >
          <TableIconTD>{getIcon(sentilinkIdTheftScore)}</TableIconTD>
          <TableTD>
            <span>ID Theft Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              Likelihood that the account is a victim of identity theft.
            </HelpIcon>
          </TableTD>
          <TableTD>
            {isRedacted ? redactedSticker : `${sentilinkIdTheftScore} / ${MAX_SENTILINK_SCORE}`}
          </TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--first-party-synthetic-score'
          id='risk-assessment-table__row--first-party-synthetic-score'
          key='first-party-synthetic-score'
        >
          <TableIconTD>{getIcon(sentilinkFirstPartySyntheticScore)}</TableIconTD>
          <TableTD>
            <span>First Party Synthetic Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              Likelihood that the account is a first party synthetic consisting of a true name and
              DOB but a fictitious SSN.
            </HelpIcon>
          </TableTD>
          <TableTD>
            {isRedacted
              ? redactedSticker
              : `${sentilinkFirstPartySyntheticScore} / ${MAX_SENTILINK_SCORE}`}
          </TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--third-party-synthetic-score'
          id='risk-assessment-table__row--third-party-synthetic-score'
          key='third-party-synthetic-score'
        >
          <TableIconTD>{getIcon(sentilinkThirdPartySyntheticScore)}</TableIconTD>
          <TableTD>
            <span>Third Party Synthetic Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              Likelihood that the account is a third party synthetic consisting of a name, DOB, and
              SSN that describe a fictitious person.
            </HelpIcon>
          </TableTD>
          <TableTD>
            {isRedacted
              ? redactedSticker
              : `${sentilinkThirdPartySyntheticScore} / ${MAX_SENTILINK_SCORE}`}
          </TableTD>
        </TableTR>
      </TableLayout>
    </>
  );

  const applicationContents = (
    <>
      <Card.Title>Sentilink Application</Card.Title>
      <AttributeGrid colCount={2}>
        <AttributeItem
          title='Application ID'
          value={report.attributes.result?.['application-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Customer ID'
          value={report.attributes.result?.['customer-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Transaction ID'
          value={report.attributes.result?.['transaction-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Environment'
          value={report.attributes.result?.environment}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Notes'
          value={report.attributes.result?.notes}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
      </AttributeGrid>
    </>
  );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {riskAssessmentContents}
      {applicationContents}
    </ReportResultLayout>
  );
};

export default SentilinkScores;
